import { clsx } from 'clsx'
import * as RSelect from '@radix-ui/react-select'
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { Check, ChevronDown, ChevronUp, Dot, Pencil, Undo } from './icons'

/* ------------------------------------------------------------------ */
/* Field shell: label, control slot, hint/error, prefilled + confirm  */
/* ------------------------------------------------------------------ */

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'offline'

/**
 * Where a value Sonder pre-filled stands with the client. `pending`: waiting for "Looks right". `confirmed`: they
 * agreed with it as is. `edited`: they changed it, which is their answer; Undo puts Sonder's version back.
 */
export type ReviewInfo = {
  state: 'pending' | 'confirmed' | 'edited'
  /** Show "Looks right". Off for fields we only tag, like "Things to avoid". */
  confirmable: boolean
  onConfirm?: (next: boolean) => void
  /** Present once there is a Sonder version to go back to. */
  onUndo?: () => void
  /** "Confirmed by Maria · Oct 2". */
  byline?: string
}

type FieldCtx = { id: string; describedBy?: string; invalid: boolean }
const Ctx = createContext<FieldCtx | null>(null)

export type FieldProps = {
  label: string
  /** Visually hide the label (still associated). Use only when context makes the purpose obvious. */
  labelHidden?: boolean
  hint?: ReactNode
  error?: string
  /** For a value Sonder pre-filled, in the client's view: the tag above, and Looks right or Undo below. */
  review?: ReviewInfo
  saveStatus?: SaveStatus
  optional?: boolean
  /** The field's path in its section document; the prefill "Still empty" list jumps to it. */
  anchor?: string
  /** A small action for what's in the box, e.g. "Copy to…", right-aligned just under it. */
  action?: ReactNode
  className?: string
  children: ReactNode
}

export function Field({ label, labelHidden, hint, error, review, saveStatus, optional, anchor, action, className, children }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errId = error ? `${id}-err` : undefined
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined
  return (
    <Ctx.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div data-field={anchor} className={clsx('group/field grid gap-1.5', className)}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <label htmlFor={id} className={clsx('text-eyebrow uppercase tracking-eyebrow font-medium text-ink', labelHidden && 'sr-only')}>
            {label}
            {optional && <span className="ml-2 font-normal text-dim normal-case tracking-normal">Optional</span>}
          </label>
          <div className="flex items-center gap-3">
            {review && <ReviewTag state={review.state} />}
            {saveStatus && saveStatus !== 'idle' && <SaveState status={saveStatus} />}
          </div>
        </div>
        {children}
        {action && <div className="flex justify-end">{action}</div>}
        {hint && !error && (
          <p id={hintId} className="text-small text-dim">
            {hint}
          </p>
        )}
        {error && (
          <p id={errId} role="alert" className="text-small text-danger">
            {error}
          </p>
        )}
        {review && <ReviewBar review={review} />}
      </div>
    </Ctx.Provider>
  )
}

export function useControl() {
  const ctx = useContext(Ctx)
  return ctx ?? { id: undefined, describedBy: undefined, invalid: false }
}

export const controlSkin =
  'bg-transparent border-0 border-b text-ink placeholder:text-dim ' +
  'transition-colors duration-150 focus:outline-none focus:border-ink disabled:text-dim disabled:cursor-not-allowed'
const controlBase = `w-full py-2 text-body ${controlSkin}`

/* ------------------------------------------------------------------ */
/* Controls                                                            */
/* ------------------------------------------------------------------ */

/** `prefixText` shows fixed text (an @, a domain) inside the underline that the person cannot edit or delete. */
export function Input({ className, prefixText, ...rest }: ComponentProps<'input'> & { prefixText?: string }) {
  const { id, describedBy, invalid } = useControl()
  if (prefixText) {
    return (
      <div
        className={clsx('flex cursor-text items-baseline border-b transition-colors duration-150 focus-within:border-ink', invalid ? 'border-danger' : 'border-rule', className)}
        onMouseDown={(e) => {
          if (!(e.target instanceof HTMLInputElement)) {
            e.preventDefault()
            e.currentTarget.querySelector('input')?.focus()
          }
        }}
      >
        <span aria-hidden="true" className="select-none py-2 text-body text-dim">{prefixText}</span>
        <input id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} className={clsx(controlBase, 'min-w-0 flex-1 border-b-0')} {...rest} />
      </div>
    )
  }
  return (
    <input
      id={id}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={clsx(controlBase, invalid ? 'border-danger' : 'border-rule', className)}
      {...rest}
    />
  )
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /** Grow with the text, from `rows` lines up, so the whole answer shows without scrolling inside the box. */
  autoGrow?: boolean
  /**
   * A one-line answer that wraps instead of running out of sight, so a long pre-filled value can be read in full
   * (on a phone especially) before confirming it. Enter adds no line break; on a touch keyboard it closes the keyboard.
   */
  singleLine?: boolean
}

export function Textarea({ className, rows = 3, autoGrow, singleLine, onChange, onKeyDown, ...rest }: TextareaProps) {
  const { id, describedBy, invalid } = useControl()
  const ref = useRef<HTMLTextAreaElement>(null)
  const grow = autoGrow || singleLine
  const fit = useCallback(() => {
    const el = ref.current
    if (!el || !grow) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`
  }, [grow])
  // Refit when the value changes from outside (a sync, Undo) and when the box changes width (rotating a phone).
  useLayoutEffect(fit, [fit, rest.value])
  useEffect(() => {
    const el = ref.current
    if (!el || !grow) return
    let width = el.clientWidth
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === width) return
      width = el.clientWidth
      fit()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [grow, fit])
  return (
    <textarea
      ref={ref}
      id={id}
      rows={singleLine ? 1 : rows}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      enterKeyHint={singleLine ? 'done' : undefined}
      onChange={(e) => {
        if (singleLine && /[\r\n]/.test(e.target.value)) e.target.value = e.target.value.replace(/\s*[\r\n]+\s*/g, ' ')
        onChange?.(e)
        fit()
      }}
      onKeyDown={(e) => {
        if (singleLine && e.key === 'Enter') {
          e.preventDefault()
          if (window.matchMedia('(pointer: coarse)').matches) e.currentTarget.blur()
        }
        onKeyDown?.(e)
      }}
      className={clsx(controlBase, 'leading-relaxed', grow ? 'resize-none overflow-y-hidden' : 'resize-y', invalid ? 'border-danger' : 'border-rule', className)}
      {...rest}
    />
  )
}

/** `divider` draws a rule above the option, e.g. to set an action like "Add a new contact" apart from the choices. */
export type SelectOption<T extends string = string> = { value: T; label: ReactNode; disabled?: boolean; divider?: boolean }

export type SelectProps<T extends string> = {
  value?: T | ''
  defaultValue?: T
  onValueChange?: (value: T) => void
  options: readonly SelectOption<T>[]
  /** Shown when value is '' (nothing chosen yet). */
  placeholder?: string
  name?: string
  required?: boolean
  disabled?: boolean
  size?: 'md' | 'sm'
  className?: string
  'aria-label'?: string
}

function selectTriggerClass(size: 'md' | 'sm', invalid: boolean, className?: string) {
  return clsx(
    controlSkin,
    'group/select flex cursor-pointer items-center justify-between gap-3 text-left data-[placeholder]:text-dim',
    size === 'sm' ? 'min-h-8 py-1 text-small tabular-nums' : 'w-full py-2 text-body',
    invalid ? 'border-danger' : 'border-rule hover:border-ink data-[state=open]:border-ink',
    className,
  )
}

/**
 * Branded dropdown (Radix Select). Inside a native <dialog> the menu portals into the dialog so it stays in the top layer.
 *
 * A controlled select first renders as a plain look-alike trigger and swaps in Radix (already open) on first use.
 * Each Radix Select builds every option off-screen even while closed, so pages with many long lists froze for
 * seconds: the hours builder alone has dozens of 96-option time pickers. Uncontrolled or `name`d selects mount
 * Radix straight away, since they need its internal value and hidden native <select>.
 */
export function Select<T extends string>(props: SelectProps<T>) {
  const { value, options, placeholder, name, disabled, size = 'md', className, 'aria-label': ariaLabel } = props
  const { id, describedBy, invalid } = useControl()
  const [active, setActive] = useState(name !== undefined || value === undefined)
  if (active) return <RadixSelect {...props} defaultOpen={!name && value !== undefined} />
  const selected = options.find((o) => o.value === value)
  return (
    <button
      type="button"
      role="combobox"
      aria-expanded={false}
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      id={id}
      disabled={disabled}
      data-state="closed"
      data-placeholder={selected ? undefined : ''}
      onClick={() => setActive(true)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          setActive(true)
        }
      }}
      className={selectTriggerClass(size, invalid, className)}
    >
      <span className="truncate">{selected ? selected.label : placeholder}</span>
      <span aria-hidden="true" className="shrink-0 text-ink">
        <ChevronDown size={12} strokeWidth={2} />
      </span>
    </button>
  )
}

function RadixSelect<T extends string>({ value, defaultValue, defaultOpen, onValueChange, options, placeholder, name, required, disabled, size = 'md', className, 'aria-label': ariaLabel }: SelectProps<T> & { defaultOpen: boolean }) {
  const { id, describedBy, invalid } = useControl()
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const triggerRef = useCallback((el: HTMLElement | null) => setContainer(el?.closest('dialog') ?? null), [])
  return (
    <RSelect.Root
      value={value}
      defaultValue={defaultValue}
      defaultOpen={defaultOpen}
      onValueChange={(v) => onValueChange?.(v as T)}
      name={name}
      required={required}
      disabled={disabled}
    >
      <RSelect.Trigger
        ref={triggerRef}
        id={id}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        className={selectTriggerClass(size, invalid, className)}
      >
        <span className="truncate">
          <RSelect.Value placeholder={placeholder} />
        </span>
        <RSelect.Icon className="shrink-0 text-ink transition-transform duration-150 group-data-[state=open]/select:rotate-180">
          <ChevronDown size={12} strokeWidth={2} />
        </RSelect.Icon>
      </RSelect.Trigger>
      <RSelect.Portal container={container ?? undefined}>
        <RSelect.Content
          position="popper"
          sideOffset={6}
          collisionPadding={16}
          className={clsx(
            // Never wider than the screen: on a phone a long option (a contact and all their locations) wraps instead.
            'select-menu z-50 min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] max-h-[min(20rem,var(--radix-select-content-available-height))] overflow-hidden',
            'border border-ink bg-paper text-ink shadow-[0_12px_32px_rgb(0_0_0/0.14)]',
          )}
        >
          <RSelect.ScrollUpButton className="flex h-6 items-center justify-center bg-paper text-dim">
            <ChevronUp size={12} />
          </RSelect.ScrollUpButton>
          <RSelect.Viewport className="py-1">
            {options.map((o) => [
              o.divider && <RSelect.Separator key={`${o.value}-divider`} className="my-1 h-px bg-gray-200" />,
              <RSelect.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className={clsx(
                  'relative flex cursor-pointer select-none items-center justify-between gap-4 px-4 outline-none',
                  size === 'sm' ? 'min-h-8 py-1 text-small tabular-nums' : 'min-h-10 py-2 text-body',
                  'data-[highlighted]:bg-ink data-[highlighted]:text-paper data-[disabled]:cursor-not-allowed data-[disabled]:text-dim',
                )}
              >
                <RSelect.ItemText>{o.label}</RSelect.ItemText>
                <RSelect.ItemIndicator className="shrink-0">
                  <Check size={12} strokeWidth={2.5} />
                </RSelect.ItemIndicator>
              </RSelect.Item>,
            ])}
          </RSelect.Viewport>
          <RSelect.ScrollDownButton className="flex h-6 items-center justify-center bg-paper text-dim">
            <ChevronDown size={12} />
          </RSelect.ScrollDownButton>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  )
}

/* ------------------------------------------------------------------ */
/* Affordances                                                         */
/* ------------------------------------------------------------------ */

export function PrefilledTag({ className }: { className?: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 text-[0.6875rem] uppercase tracking-eyebrow text-warn', className)}>
      <Dot size={6} />
      Pre-filled by Sonder Media House
    </span>
  )
}

/** Above a pre-filled value: Sonder's until the client changes it, then theirs. */
export function ReviewTag({ state, className }: { state: ReviewInfo['state']; className?: string }) {
  if (state !== 'edited') return <PrefilledTag className={className} />
  // Green like Confirmed: an edit is the client's answer, so the field is done. The pencil sets it apart from the
  // amber "Pre-filled" dot it replaces.
  return (
    <span className={clsx('inline-flex items-center gap-1.5 text-[0.6875rem] font-medium uppercase tracking-eyebrow text-ok', className)}>
      <Pencil size={11} strokeWidth={2} />
      Edited
    </span>
  )
}

/** Below a pre-filled value: "Looks right" (or Confirmed), or Undo once it was changed, and who did it. */
export function ReviewBar({ review, className }: { review: ReviewInfo; className?: string }) {
  const control =
    review.state === 'edited' ? (
      review.onUndo && (
        <button
          type="button"
          onClick={review.onUndo}
          title="Put back what Sonder Media House wrote"
          className="inline-flex min-h-8 items-center gap-2 border border-gray-300 px-3 text-[0.75rem] font-semibold uppercase tracking-button text-charcoal transition-colors duration-150 hover:border-ink hover:text-ink"
        >
          <Undo size={12} />
          Undo changes
        </button>
      )
    ) : review.confirmable && review.onConfirm ? (
      <ConfirmToggle checked={review.state === 'confirmed'} onChange={review.onConfirm} />
    ) : null
  if (!control && !review.byline) return null
  return (
    <div className={clsx('flex flex-wrap items-center justify-between gap-2 pt-1', className)}>
      {control ?? <span />}
      {review.byline && <span className="text-small text-dim">{review.byline}</span>}
    </div>
  )
}

export function ConfirmToggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'inline-flex min-h-8 items-center gap-2 border px-3 text-[0.75rem] font-semibold uppercase tracking-button transition-colors duration-150',
        checked ? 'border-ok bg-ok-wash text-ok' : 'border-ink bg-transparent text-ink hover:bg-ink hover:text-paper',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      <span
        aria-hidden="true"
        className={clsx('grid h-3.5 w-3.5 place-items-center border', checked ? 'border-ok bg-ok text-paper' : 'border-current')}
      >
        {checked && <Check size={10} strokeWidth={3} />}
      </span>
      {checked ? 'Confirmed' : 'Looks right'}
    </button>
  )
}

const SAVE_TEXT: Record<SaveStatus, string> = {
  idle: '',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Not saved',
  offline: 'Saved on this device',
}

export function SaveState({ status, className }: { status: SaveStatus; className?: string }) {
  if (status === 'idle') return null
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 text-[0.6875rem] uppercase tracking-eyebrow',
        status === 'saved' && 'text-ok',
        status === 'saving' && 'text-dim',
        status === 'error' && 'text-danger',
        status === 'offline' && 'text-warn',
        className,
      )}
      aria-live="polite"
    >
      <Dot size={6} className={clsx(status === 'saving' && 'animate-pulse')} />
      {SAVE_TEXT[status]}
    </span>
  )
}
